import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Avatar, Chip } from "@mui/material";
import dayjs from "dayjs";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import BaseTextField from "../../components/ui/BaseTextField";
import BaseTooltip from "../../components/ui/BaseTooltip";
import BaseDropdownV2 from "../../components/ui/BaseDropdownV2";
import BaseConfirmModal from "../../components/ui/BaseConfirmModal";
import AdminFilters from "./AdminFilters";
import { getCustomerUsers } from "../../services/api/customerUsers";
import { activateCompanyUser, deactivateCompanyUser } from "../../services/api/company";
import { showToast } from "../../store/toast/toastSlice";
import { setAdminFilters } from "../../store/filters/adminFiltersSlice";
import { debounce, formatPhoneNumber } from "../../utils/helpers";
import { joinNonEmpty } from "../../utils/displayValue";
import BaseWrapper from "../../components/ui/BaseWrapper";

const menuOptions = [
  { label: "View Profile", value: "VIEW" },
  { label: "Activate", value: "ACTIVATE" },
  { label: "Deactivate", value: "DEACTIVATE" },
];
const CustomerAccountUsersTable = ({ hideTitle = false, customerId }) => {
  const { id: routeId } = useParams();
  const id = customerId ?? routeId;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [sortModel, setSortModel] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isConfirmActivateOpen, setIsConfirmActivateOpen] = useState(false);
  const [isConfirmDeactivateOpen, setIsConfirmDeactivateOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const userFilters = useSelector((state) => state.adminFilters.customerAccountUsers);

  const filtersPayload = useMemo(
    () => ({
      fullName: userFilters.fullName || undefined,
      email: userFilters.email || undefined,
      country: userFilters.country?.label || undefined,
      status: userFilters.status || undefined,
    }),
    [userFilters],
  );

  const queryParams = {
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
    search,
    customerId: id,
    sortBy: sortModel[0]?.field,
    sortOrder: sortModel[0]?.sort,
  };

  const { data, isPending, refetch } = useQuery({
    queryKey: ["adminCustomerUsers", queryParams, filtersPayload],
    queryFn: () => getCustomerUsers({ ...queryParams, ...filtersPayload }),
    placeholderData: (prev) => prev,
  });

  const { data: userOptionsData } = useQuery({
    queryKey: ["customerUserFilterOptions", id],
    queryFn: () => getCustomerUsers({ customerId: id, limit: 500 }),
    enabled: isFilterDrawerOpen,
    staleTime: 5 * 60 * 1000,
  });

  const nameOptions = useMemo(
    () =>
      [
        ...new Set(
          (userOptionsData?.data?.data || [])
            .map((user) => `${user.firstName || ""} ${user.lastName || ""}`.trim())
            .filter(Boolean),
        ),
      ]
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name })),
    [userOptionsData],
  );

  const appliedFilters = useMemo(() => {
    const applied = [];
    if (userFilters.fullName)
      applied.push({ prefix: "Name", value: userFilters.fullName, key: "fullName" });
    if (userFilters.email)
      applied.push({ prefix: "Email", value: userFilters.email, key: "email" });
    if (userFilters.country?.label)
      applied.push({ prefix: "Country", value: userFilters.country.label, key: "country" });
    if (userFilters.status) {
      const statusLabelMap = {
        PENDING: "Pending",
        SUBMITTED: "Submitted",
        APPROVED: "Active",
        ACTIVATED: "Active",
        DEACTIVATED: "In-Active",
      };
      applied.push({
        prefix: "Status",
        value: statusLabelMap[userFilters.status] || userFilters.status,
        key: "status",
      });
    }
    return applied;
  }, [userFilters]);

  const handleRemoveFilter = (key) => {
    dispatch(
      setAdminFilters({
        module: "customerAccountUsers",
        filterName: key,
        data: key === "country" ? { value: "", label: "" } : "",
      }),
    );
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const { mutateAsync: activateUser, isPending: isActivating } = useMutation({
    mutationFn: activateCompanyUser,
    onSuccess: () => {
      dispatch(showToast({ message: "User activated successfully", type: "success" }));
      refetch();
      setIsConfirmActivateOpen(false);
    },
    onError: (error) => {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to activate user",
          type: "error",
        }),
      );
    },
  });

  const { mutateAsync: deactivateUser, isPending: isDeactivating } = useMutation({
    mutationFn: deactivateCompanyUser,
    onSuccess: () => {
      dispatch(showToast({ message: "User deactivated successfully", type: "success" }));
      refetch();
      setIsConfirmDeactivateOpen(false);
    },
    onError: (error) => {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to deactivate user",
          type: "error",
        }),
      );
    },
  });

  const handleItemClick = (item, row) => {
    setSelectedUser(row);
    if (item === "VIEW") {
      navigate(`/user/details/profile/${row.id}`);
    } else if (item === "ACTIVATE") {
      setIsConfirmActivateOpen(true);
    } else if (item === "DEACTIVATE") {
      setIsConfirmDeactivateOpen(true);
    }
  };

  const users = data?.data?.data || [];
  const rowCount = data?.data?.totalCount || 0;
  const customerAddress = data?.data?.customer?.address;

  const debouncedSearchRef = useRef(
    debounce((value) => {
      setSearch(value.trim());
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500),
  );

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    debouncedSearchRef.current(e.target.value);
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => <p className="text-primary cursor-pointer">{params?.value}</p>,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1.6,
      minWidth: 190,
      renderCell: (params) => (
        <div className="flex items-center gap-2 cursor-pointer">
          <Avatar sx={{ width: 28, height: 28, fontSize: 12 }} src={params.row.profilePicture}>
            {params.row.firstName?.[0]}
            {params.row.lastName?.[0]}
          </Avatar>
          <p className="text-primary capitalize">
            {params.row.firstName} {params.row.lastName}
          </p>
          {params.row.isOwner && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">
              Owner
            </span>
          )}
        </div>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1.8,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => {
        // Customer accounts are created from the company's details and never fill a
        // personal address, so the customer's own address stands in for them.
        const personal = params.row.personalAddress;
        const address = personal?.addressLine1 ? personal : customerAddress;
        const fullAddress = joinNonEmpty([address?.addressLine1, address?.country]);

        if (!fullAddress) return <p>-</p>;

        return (
          <BaseTooltip placement="top" content={<p className="text-white">{fullAddress}</p>}>
            <p className="truncate overflow-hidden whitespace-nowrap">{address?.addressLine1}</p>
          </BaseTooltip>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.6,
      minWidth: 190,
      sortable: false,
      renderCell: (params) => <p>{params?.value || "-"}</p>,
    },
    {
      field: "phoneNumber",
      headerName: "Mobile",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => <p>{formatPhoneNumber(params?.value) || "-"}</p>,
    },
    {
      field: "createdAt",
      headerName: "Date",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => <p>{dayjs(params.value).format("DD-MM-YYYY")}</p>,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.9,
      minWidth: 110,
      renderCell: (params) =>
        params.row.status === "APPROVED" || params.row.status === "ACTIVATED" ? (
          <Chip label="Active" color="success" size="small" className="w-24 justify-center" />
        ) : params.row.status === "DEACTIVATED" ? (
          <Chip label="In-Active" color="error" size="small" className="w-24 justify-center" />
        ) : (
          <Chip
            label={params.row.status === "SUBMITTED" ? "Submitted" : "Pending"}
            color="primary"
            size="small"
            variant="outlined"
            className="w-24 justify-center"
          />
        ),
    },
    // Activating or deactivating a customer account is done from the Customers
    // listing itself, so the row actions are kept out of this view.
    // {
    //   field: "actions",
    //   headerName: "Actions",
    //   flex: 0.7,
    //   minWidth: 90,
    //   sortable: false,
    //   renderCell: (params) => {
    //     const filteredMenuOptions = menuOptions.filter((item) => {
    //       if (item.value === "ACTIVATE" && ["APPROVED", "ACTIVATED"].includes(params.row.status))
    //         return false;
    //       if (item.value === "DEACTIVATE" && params.row.status === "DEACTIVATED") return false;
    //       return true;
    //     });
    //     return (
    //       <BaseDropdownV2
    //         menuOptions={filteredMenuOptions}
    //         triggerElement={<i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
    //         onItemClick={(item) => handleItemClick(item, params.row)}
    //       />
    //     );
    //   },
    // },
  ];

  const handleCellClick = (params) => {
    if (params.field === "id" || params.field === "name") {
      navigate(`/user/details/profile/${params.row.id}`);
    }
  };

  return (
    <>
      {!hideTitle && (
        <div className="border-b border-zinc-300 bg-white">
          <BaseWrapper>
            <div className="grid grid-cols-7 gap-y-3 py-[7px] items-center">
              <div className="md:col-span-3 col-span-7">
                <h3 className=" md:text-left text-center">
                  {data?.data?.customer?.clientName
                    ? `${data.data.customer.clientName}'s Users`
                    : "Customer Users"}
                </h3>
              </div>
            </div>
          </BaseWrapper>
        </div>
      )}

      <BaseWrapper>
        <div
          className={`bg-white rounded-lg px-5 py-3.5 shadow-md sticky top-10 z-10 ${
            hideTitle ? "" : "mt-4"
          }`}
        >
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 w-full">
            <div
              className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5 cursor-pointer"
              onClick={() => setIsFilterDrawerOpen(true)}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary">
                <i className="fa-light fa-filter text-white text-sm"></i>
              </span>
              <p className="text-primary ml-2 ">Add Filter</p>
            </div>
            <div className="flex-1 min-w-0 pl-3 overflow-x-auto whitespace-nowrap py-1 scrollbar">
              <div className="flex gap-2">
                {appliedFilters.map((f) => (
                  <div
                    key={f.key}
                    className="flex-shrink-0 flex items-center bg-[#EAF1FF] text-sm font-medium rounded-md px-3 py-1"
                  >
                    <span className="text-primary">{f.prefix}:</span>&nbsp;
                    <p className="text-primary text-sm mx-1">{f.value}</p>
                    <button
                      onClick={() => handleRemoveFilter(f.key)}
                      className="ml-2 text-primary hover:text-red-500"
                      aria-label={`Remove ${f.prefix} ${f.value}`}
                    >
                      <i className="fas fa-times text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full sm:w-[28%]">
              <BaseTextField
                id="customerUsersSearch"
                name="customerUsersSearch"
                type="search"
                autoComplete="off"
                placeholder="Search by name, email or ID"
                value={searchQuery}
                onChange={handleSearchChange}
                startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
              />
            </div>
          </div>
        </div>

        <BaseDataTableV2
          className="max-h-[calc(100vh-320px)]"
          rowHeight={38}
          columns={columns}
          data={users}
          isLoading={isPending}
          onCellClick={handleCellClick}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          hideFooter={false}
          disableColumnSorting={false}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
        />

        {isFilterDrawerOpen && (
          <AdminFilters
            open={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            fetchData={refetch}
            isLoading={isPending}
            setFiltersChanged={() => {}}
            entity="customerAccountUsers"
            nameOptions={nameOptions}
          />
        )}

        {isConfirmActivateOpen && (
          <BaseConfirmModal
            open={isConfirmActivateOpen}
            onClose={() => setIsConfirmActivateOpen(false)}
            onConfirm={() => activateUser(String(selectedUser?.id))}
            isLoading={isActivating}
            title="Are you sure you want to activate this user?"
            description="Once activated, the user will regain access and its status will be set to Active."
            confirmText="Activate"
            confirmButtonColor="primary"
          />
        )}

        {isConfirmDeactivateOpen && (
          <BaseConfirmModal
            open={isConfirmDeactivateOpen}
            onClose={() => setIsConfirmDeactivateOpen(false)}
            onConfirm={() => deactivateUser(String(selectedUser?.id))}
            isLoading={isDeactivating}
            title="Are you sure you want to deactivate this user?"
            description="Once deactivated, the user will lose access and its status will be set to Inactive."
            confirmText="Deactivate"
            confirmButtonColor="error"
          />
        )}
      </BaseWrapper>
    </>
  );
};

export default CustomerAccountUsersTable;
